// Cockpit revamp — right-rail status lifecycle card.
//
// Renders the stable five-state lifecycle (draft → active → paused →
// enrolment_closed → completed/force_ended) as a vertical timeline,
// plus a "valid from <status>" pill list that mirrors the matrix.
import React from 'react';

import { Pill } from '../../../ui';
import type { CampaignStatus } from '../../../types/campaign';
import { validTransitionsOf } from './transitions';

const LIFECYCLE: CampaignStatus[] = [
  'draft',
  'active',
  'paused',
  'enrolment_closed',
  'force_ended',
];

interface CampaignStatusLifecycleProps {
  current: CampaignStatus;
}

const CampaignStatusLifecycle: React.FC<CampaignStatusLifecycleProps> = ({
  current,
}) => {
  const validNext = validTransitionsOf(current);
  return (
    <div>
      <ul className="flex flex-col gap-2" role="list">
        {LIFECYCLE.map((s) => {
          const active = s === current;
          return (
            <li key={s} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={[
                  'w-2.5 h-2.5 rounded-full flex-none',
                  active ? 'bg-ok' : 'bg-white/[0.08]',
                ].join(' ')}
              />
              <span
                className={[
                  'text-xs font-mono',
                  active ? 'text-slate-100' : 'text-slate-500',
                ].join(' ')}
              >
                {s}
              </span>
              {active ? (
                <Pill intent="ok" className="ml-auto">
                  now
                </Pill>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="mt-3 pt-3 border-t border-white/[0.06]">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
          valid from <code>{current}</code>
        </div>
        {validNext.length === 0 ? (
          <span className="text-xs text-slate-500 italic">none · terminal</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {validNext.map((v) => (
              <Pill key={v.kind} intent="info">
                → {v.target}
              </Pill>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignStatusLifecycle;
